import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACTS } from "@/data";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useLang();
  const options = t.contact.serviceOptions;
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_type: options[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t.contact.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      toast.success(t.contact.success);
      setForm({ name: "", phone: "", email: "", service_type: options[0], message: "" });
    } catch (err) {
      toast.error(t.contact.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#d4af37]/25 px-4 py-3.5 text-white placeholder-white/35 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/40 transition-colors duration-300";

  return (
    <section id="contact" className="relative py-28 md:py-40 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left info */}
        <div className="lg:col-span-5">
          <span className="text-[#d4af37] uppercase tracking-[0.3em] text-xs flex items-center gap-3">
            <span className="h-px w-10 bg-[#d4af37]" /> {t.contact.eyebrow}
          </span>
          <h2 className="mt-5 font-serif-lux font-light text-4xl sm:text-6xl tracking-tight text-white leading-[1]">
            {t.contact.titleA} <span className="italic text-[#d4af37]">{t.contact.titleB}</span>
          </h2>
          <p className="mt-6 text-white/60 font-light max-w-md">
            {t.contact.intro}
          </p>

          <div className="mt-10 space-y-5">
            {CONTACTS.map((c) => (
              <a
                key={c.tel}
                href={`tel:${c.tel}`}
                data-testid={`contact-phone-${c.name.toLowerCase()}`}
                className="group flex items-center gap-4 border border-[#d4af37]/20 p-5 hover:border-[#d4af37]/60 hover:bg-[#0d0d0d] transition-colors duration-300"
              >
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#d4af37]/10 text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-black transition-colors duration-300">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-white/50">{c.name}</span>
                  <span className="block text-lg text-white font-serif-lux">{c.phone}</span>
                </span>
              </a>
            ))}
            <div className="flex items-center gap-4 p-5 text-white/70">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#d4af37]/10 text-[#d4af37]">
                <MapPin size={18} />
              </span>
              <span className="font-light">{t.contact.serviceArea}</span>
            </div>
          </div>
        </div>

        {/* Right form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 border border-[#d4af37]/20 p-8 md:p-10 bg-[#080808]"
          data-testid="estimate-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{t.contact.name} *</label>
              <input data-testid="input-name" className={inputCls} value={form.name} onChange={update("name")} placeholder={t.contact.namePh} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{t.contact.phone} *</label>
              <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder={t.contact.phonePh} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{t.contact.email}</label>
              <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder={t.contact.emailPh} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{t.contact.service}</label>
              <select data-testid="input-service" className={`${inputCls} appearance-none cursor-pointer`} value={form.service_type} onChange={update("service_type")}>
                {options.map((s) => (
                  <option key={s} value={s} className="bg-[#0d0d0d] text-white">{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">{t.contact.details}</label>
            <textarea data-testid="input-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={update("message")} placeholder={t.contact.detailsPh} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-estimate-button"
            className="group mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#d4af37] text-black px-9 py-4 uppercase tracking-[0.15em] text-sm font-medium gold-glow disabled:opacity-60 hover:bg-[#f1e5ac] transition-colors duration-300"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> {t.contact.sending}</> : <>{t.contact.submit} <Send size={16} /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
